/* TourDetailGrid — co-located styles (the project keeps component CSS next to
   the component, NOT in the global styles.css). Tuned for an iPad: large tap
   targets (>=44px), no hover-only affordances, a single readable column of cards
   that matches the TourListView card look (surface, --line border, --radius). */

.tour-detail-grid {
  list-style: none;
  margin: 0;
  padding: 0;
  display: flex;
  flex-direction: column;
  gap: 12px;
}

/* One artwork = one card row. Mirrors the .tour-card surface treatment. */
.tdg-row {
  display: flex;
  align-items: center;
  gap: 14px;
  padding: 12px 14px;
  background: var(--surface);
  border: 1px solid var(--line);
  border-radius: var(--radius);
  /* Smooth out the drag re-order so insertion shifts read clearly. */
  transition: box-shadow 0.12s ease, border-color 0.12s ease, opacity 0.12s ease;
}

/* The row currently being dragged: the floating ghost (below) is the "lifted"
   copy, so the origin reads as the empty slot it left — a faded, dashed gap. */
.tdg-row--dragging {
  opacity: 0.4;
  background: var(--surface-2);
  border-style: dashed;
  border-color: var(--accent);
  box-shadow: none;
}

/* The row the pointer is hovering over while dragging: where it will land. */
.tdg-row--drop {
  border-color: var(--accent);
  box-shadow: inset 0 0 0 2px var(--accent);
}

/* ---------- Floating drag preview ("ghost") ----------
   A translucent copy of the dragged row that tracks the pointer, so it's
   unmistakable that a reorder is in progress — especially on touch, where the
   finger covers the handle. Portaled to <body>, positioned via inline
   left/top/width, and purely visual (pointer-events: none). The translucency
   lets the shifting list read underneath it. */
.tdg-ghost {
  position: fixed;
  z-index: 1000;
  display: flex;
  align-items: center;
  gap: 14px;
  padding: 12px 14px;
  background: var(--surface);
  border: 2px solid var(--accent);
  border-radius: var(--radius);
  box-shadow: 0 12px 30px rgb(31 41 51 / 0.28);
  opacity: 0.85;
  pointer-events: none;
}
.tdg-ghost__cover {
  flex: 0 0 auto;
  width: 96px;
  height: 96px;
  border-radius: 10px;
  overflow: hidden;
  background: var(--surface-2);
}
.tdg-ghost__label {
  flex: 1 1 auto;
  min-width: 0;
  font-weight: 700;
  font-size: 17px;
  line-height: 1.25;
  /* Match the row label: allow two lines, then clamp. */
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
}

/* ---------- Drag handle (Pointer-Events drag) ----------
   A tall, obvious grip that spans the row height so it's easy to grab and drag on
   an iPad — while the rest of the row (thumbnail, text) stays free for scrolling. */
.tdg-row__handle {
  flex: 0 0 auto;
  align-self: stretch; /* full row height -> a big, unmissable drag target */
  display: grid;
  place-items: center;
  min-width: 40px;
  padding: 0;
  border: 1px solid var(--line);
  border-radius: 10px;
  background: var(--surface-2);
  color: var(--ink-faint);
  font-size: 24px;
  line-height: 1;
  cursor: grab;
  /* Critical for Pointer-Events drag on iPad: stop the browser from scrolling
     when the gesture starts on the handle. */
  touch-action: none;
}
.tdg-row__handle:active {
  cursor: grabbing;
  background: var(--surface);
  color: var(--ink-soft);
}
.tdg-row__handle:disabled {
  opacity: 0.5;
  cursor: default;
}

/* ---------- Thumbnail (the artwork — the primary visual cue) ---------- */
.tdg-row__cover {
  flex: 0 0 auto;
  width: 96px;
  height: 96px;
  border-radius: 10px;
  overflow: hidden;
  background: var(--surface-2);
}
.tdg-row__img {
  width: 100%;
  height: 100%;
  object-fit: cover;
  border-radius: 0;
  max-height: none;
}

/* ---------- Quote-slide row ----------
   A quote slide reads as a card like any other, but its "cover" is a dark swatch
   with a gold serif quotation mark — a small echo of how it renders on the iPad
   (dark, serif), so a non-technical user can tell photo cards from quote cards at
   a glance. */
.tdg-row__cover--quote {
  display: grid;
  place-items: center;
  background: #181818;
  border: 1px solid #2c2c2c;
}
.tdg-row__quote-mark {
  font-family: Georgia, "Times New Roman", serif;
  font-size: 54px;
  line-height: 0.7;
  color: #c9b27a; /* muted gold, matching the export's quote cells */
}
/* The "Quote slide · N quotes" sub-line under the label. */
.tdg-row__kind {
  font-size: 14px;
  color: var(--ink-soft);
  font-style: italic;
}

/* ---------- Meta (position, label, artist, error) ---------- */
.tdg-row__meta {
  flex: 1 1 auto;
  min-width: 0;
  display: flex;
  flex-direction: column;
  gap: 2px;
}
.tdg-row__pos {
  font-size: 12px;
  font-weight: 700;
  color: var(--ink-faint);
  letter-spacing: 0.02em;
}
.tdg-row__label {
  font-weight: 700;
  font-size: 17px;
  line-height: 1.25;
  /* Allow a long title two lines, then clamp. */
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
}
.tdg-row__artist {
  font-size: 14px;
  color: var(--ink-soft);
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}
/* Inline, retry-able error surfaced when a remove rejects. */
.tdg-row__error {
  margin-top: 4px;
  align-self: flex-start;
  padding: 6px 10px;
  text-align: left;
  font-size: 13px;
  color: var(--danger);
  background: var(--error-bg);
  border: 1px solid var(--error-line);
  border-radius: 8px;
}

/* ---------- Actions (reorder buttons + remove) ---------- */
.tdg-row__actions {
  flex: 0 0 auto;
  display: flex;
  flex-direction: column;
  align-items: stretch;
  gap: 8px;
}
.tdg-row__move {
  display: flex;
  gap: 8px;
}
/* Reorder buttons: keep them comfortably tappable and from squashing their
   label on a phone-width column. */
.tdg-row__btn {
  flex: 1 1 auto;
  min-height: var(--tap);
  white-space: nowrap;
}
.tdg-row__remove {
  min-height: var(--tap);
  white-space: nowrap;
}

/* ---------- Narrow screens (phone / split-view iPad) ----------
   Stack the actions under the meta so nothing gets crushed; the card itself
   wraps its action block to a full-width row. */
@media (max-width: 560px) {
  .tdg-row {
    flex-wrap: wrap;
  }
  .tdg-row__actions {
    flex-basis: 100%;
    flex-direction: row;
    flex-wrap: wrap;
  }
  .tdg-row__move {
    flex: 1 1 auto;
  }
  .tdg-row__remove {
    flex: 1 1 100%;
  }
}
